'use client';

import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE_CLASSES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
};

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  size = 'md',
}: ModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div data-component-id="src\components\ui\Modal.tsx:47:4" data-component-path="src\components\ui\Modal.tsx" data-component-line="47" data-component-end-line="77" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20flex%20items-center%20justify-center%20p-4%22%7D"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div data-component-id="src\components\ui\Modal.tsx:53:6" data-component-path="src\components\ui\Modal.tsx" data-component-line="53" data-component-end-line="56" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F70%20backdrop-blur-sm%20animate-fade-in%22%7D"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div data-component-id="src\components\ui\Modal.tsx:57:6" data-component-path="src\components\ui\Modal.tsx" data-component-line="57" data-component-end-line="76" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`relative w-full ${SIZE_CLASSES[size]} bg-surface-elevated border border-border rounded-xl shadow-modal animate-slide-up max-h-[90vh] flex flex-col`}
      >
        <div data-component-id="src\components\ui\Modal.tsx:60:8" data-component-path="src\components\ui\Modal.tsx" data-component-line="60" data-component-end-line="74" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20p-5%20border-b%20border-border%20shrink-0%22%7D" className="flex items-start justify-between p-5 border-b border-border shrink-0">
          <div data-component-id="src\components\ui\Modal.tsx:61:10" data-component-path="src\components\ui\Modal.tsx" data-component-line="61" data-component-end-line="66" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <h2 data-component-id="src\components\ui\Modal.tsx:62:12" data-component-path="src\components\ui\Modal.tsx" data-component-line="62" data-component-end-line="64" data-component-file="Modal.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22id%22%3A%22modal-title%22%2C%22className%22%3A%22text-base%20font-600%20text-white%22%7D" id="modal-title" className="text-base font-600 text-white">
              {title}
            </h2>
            {subtitle && <p data-component-id="src\components\ui\Modal.tsx:65:25" data-component-path="src\components\ui\Modal.tsx" data-component-line="65" data-component-end-line="65" data-component-file="Modal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-0.5%22%7D" className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <button data-component-id="src\components\ui\Modal.tsx:67:10" data-component-path="src\components\ui\Modal.tsx" data-component-line="67" data-component-end-line="73" data-component-file="Modal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-muted-foreground%20hover%3Atext-white%20transition-colors%20p-1%20rounded%20hover%3Abg-surface%22%7D"
            onClick={onClose}
            className="text-muted-foreground hover:text-white transition-colors p-1 rounded hover:bg-surface"
            aria-label="Close modal"
          >
            <X data-component-id="src\components\ui\Modal.tsx:72:12" data-component-path="src\components\ui\Modal.tsx" data-component-line="72" data-component-end-line="72" data-component-file="Modal.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%7D" size={16} />
          </button>
        </div>
        <div data-component-id="src\components\ui\Modal.tsx:75:8" data-component-path="src\components\ui\Modal.tsx" data-component-line="75" data-component-end-line="75" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22overflow-y-auto%20scrollbar-thin%20flex-1%20p-5%22%7D" className="overflow-y-auto scrollbar-thin flex-1 p-5">{children}</div>
      </div>
    </div>
  );
}
